"use client";
import React, { useState } from "react";
import { Brain, Send, Loader2, CheckCircle2, Sparkles } from "lucide-react";

export const InteractiveBackendChat = () => {
    const [message, setMessage] = useState("");
    const [status, setStatus] = useState<"idle" | "processing" | "completed">("idle");
    const [chatHistory, setChatHistory] = useState<Array<{ role: "user" | "assistant"; content: string }>>([
        {
            role: "assistant",
            content: "Hello! I'm your Backend Architecture Assistant. I can help you design endpoints, services, functions, and middlewares. What would you like to build?"
        }
    ]);

    const examplePrompts = [
        "Create a user registration endpoint with email validation",
        "Add a payment service with Stripe integration",
        "Build an authentication middleware using JWT"
    ];

    const handleSend = () => {
        if (!message.trim() || status === "processing") return;

        // Add user message
        setChatHistory([...chatHistory, { role: "user", content: message }]);
        setMessage("");
        setStatus("processing");

        // Simulate AI response
        setTimeout(() => {
            const responses = [
                "I've created a POST /api/auth/register endpoint with:\n• Email validation middleware\n• Password hashing service\n• User creation function\n• JWT token generation",
                "I've designed a PaymentService with:\n• Stripe SDK integration\n• processPayment() function\n• refundPayment() function\n• Dependencies: UserService, OrderService",
                "I've built a JWT Authentication middleware with:\n• Token verification function\n• User session management\n• 401/403 error responses\n• Execution order: 1 (runs first)"
            ];
            const randomResponse = responses[Math.floor(Math.random() * responses.length)];

            setChatHistory(prev => [...prev, { role: "assistant", content: randomResponse }]);
            setStatus("completed");

            setTimeout(() => setStatus("idle"), 1000);
        }, 2000);
    };

    const handleExampleClick = (prompt: string) => {
        setMessage(prompt);
    };

    return (
        <div className="w-full max-w-3xl mx-auto bg-surface border border-border rounded-xl overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="bg-background/50 backdrop-blur-sm p-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-primary/10 rounded-lg">
                        <Brain className="w-4 h-4 text-primary" />
                    </div>
                    <span className="font-semibold text-primary-text text-sm">Backend Chat Agent</span>
                </div>
                <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/20 border border-red-500/50" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/20 border border-yellow-500/50" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/20 border border-green-500/50" />
                </div>
            </div>

            {/* Chat Messages */}
            <div className="h-80 overflow-y-auto p-6 space-y-4 bg-gradient-to-b from-surface to-background">
                {chatHistory.map((msg, index) => (
                    <div
                        key={index}
                        className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                        {msg.role === "assistant" && (
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                <Brain className="w-4 h-4 text-primary" />
                            </div>
                        )}
                        <div
                            className={`max-w-[80%] rounded-xl p-4 ${msg.role === "user"
                                    ? "bg-primary text-white"
                                    : "bg-background border border-border text-primary-text"
                                }`}
                        >
                            <div className="text-sm whitespace-pre-line">{msg.content}</div>
                        </div>
                        {msg.role === "user" && (
                            <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center shrink-0">
                                <div className="w-4 h-4 rounded-full bg-secondary" />
                            </div>
                        )}
                    </div>
                ))}

                {status === "processing" && (
                    <div className="flex gap-3 justify-start">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <Brain className="w-4 h-4 text-primary" />
                        </div>
                        <div className="bg-background border border-border rounded-xl p-4">
                            <div className="flex items-center gap-2">
                                <Loader2 className="w-4 h-4 text-primary animate-spin" />
                                <span className="text-sm text-secondary-text">Analyzing architecture...</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Example Prompts */}
            {chatHistory.length === 1 && status === "idle" && (
                <div className="px-6 pb-4 space-y-2">
                    <div className="text-xs font-medium text-secondary-text uppercase tracking-wider mb-2">
                        Try these examples:
                    </div>
                    <div className="space-y-2">
                        {examplePrompts.map((prompt, index) => (
                            <button
                                key={index}
                                onClick={() => handleExampleClick(prompt)}
                                className="w-full text-left p-3 bg-background border border-border rounded-lg text-sm text-secondary-text hover:border-primary/30 hover:text-primary-text transition-all group"
                            >
                                <div className="flex items-center gap-2">
                                    <Sparkles className="w-3 h-3 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                                    <span>{prompt}</span>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Input Area */}
            <div className="p-4 border-t border-border bg-background/50">
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && handleSend()}
                        placeholder="Describe what you want to build..."
                        className="flex-1 bg-background border border-border rounded-lg px-4 py-3 text-sm text-primary-text placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                        disabled={status === "processing"}
                    />
                    <button
                        onClick={handleSend}
                        disabled={!message.trim() || status === "processing"}
                        className="px-4 py-3 bg-primary hover:bg-primary/90 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary/20"
                    >
                        {status === "processing" ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                        ) : status === "completed" ? (
                            <CheckCircle2 className="w-5 h-5" />
                        ) : (
                            <Send className="w-5 h-5" />
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};
